import React, { useState, useEffect } from 'react';
import { MessageCircle, Disc3, Sliders, X } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';

const STORAGE_KEY = 'pulse_studio_onboarding_done';

export default function StudioOnboarding({ visible, onDismiss, onProducerTourFinished }) {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);

  const STEPS = [
    {
      id: 'chat',
      title: t('onboarding.step1_title'),
      subtitle: t('onboarding.step1_subtitle'),
      description: t('onboarding.step1_desc'),
      icon: MessageCircle,
      color: '#F5C842',
    },
    {
      id: 'generate',
      title: t('onboarding.step2_title'),
      subtitle: t('onboarding.step2_subtitle'),
      description: t('onboarding.step2_desc'),
      icon: Disc3,
      color: '#00D4FF',
    },
    {
      id: 'iterate',
      title: t('onboarding.step3_title'),
      subtitle: t('onboarding.step3_subtitle'),
      description: t('onboarding.step3_desc'),
      icon: Sliders,
      color: '#10B981',
    },
  ];

  useEffect(() => {
    if (!visible) return;
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) setShow(true);
  }, [visible]);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, '1');
    onProducerTourFinished?.();
    onDismiss?.();
  };

  if (!show) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const stepLabel = t('onboarding.step_of').replace('{step}', step + 1).replace('{total}', STEPS.length);

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="studio-onboarding">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-auto" onClick={handleDismiss} />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
        <div
          className="relative w-[340px] max-w-[90vw] rounded-2xl border border-[#27272A] bg-[#0A0A0D] p-6 shadow-2xl"
          data-testid="onboarding-card"
          style={{ boxShadow: `0 0 80px ${current.color}15, 0 0 40px ${current.color}08` }}
        >
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#1A1A1F] flex items-center justify-center text-[#52525B] hover:text-white transition-colors"
            data-testid="onboarding-skip"
          >
            <X size={12} />
          </button>

          <div className="flex gap-1.5 mb-5">
            {STEPS.map((_, i) => (
              <div key={i} className="h-0.5 flex-1 rounded-full transition-all duration-500"
                style={{ background: i <= step ? current.color : '#27272A' }} />
            ))}
          </div>

          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{ background: `${current.color}15`, border: `1px solid ${current.color}30` }}>
            <Icon size={22} style={{ color: current.color }} />
          </div>

          <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: current.color }}>
            {stepLabel}
          </p>
          <h3 className="text-lg font-bold text-white mb-1">{current.title}</h3>
          <p className="text-xs text-[#71717A] mb-1 font-medium">{current.subtitle}</p>
          <p className="text-sm text-[#A1A1AA] leading-relaxed mt-3">{current.description}</p>

          <div className="flex items-center justify-between mt-6">
            <button
              onClick={handleDismiss}
              className="text-[10px] text-[#52525B] hover:text-white transition-colors uppercase tracking-widest"
              data-testid="onboarding-dismiss"
            >
              {t('onboarding.skip')}
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-2 rounded-lg text-xs font-semibold text-black transition-all hover:scale-105"
              style={{ background: current.color }}
              data-testid="onboarding-next"
            >
              {step < STEPS.length - 1 ? t('onboarding.next') : t('onboarding.lets_go')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
